import { useCallback, useMemo } from 'react';
import { getText } from 'i18n/intl';
import {
  hubStudentReservationSettingOnChange,
  hubStudentSettingsErrorsSelector,
  hubStudentSettingsSelector,
  hubStudentSettingsValidate,
} from 'slices/hubStudentSettings.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { EReservationStatuses } from '@timeedit/types/enums';
import { Segmented, TEInput } from '@timeedit/ui-components';
import { Select, SelectProps } from '@timeedit/ui-components/antd';
import intl from '../../../../../i18n/intl';
import { HubStudentReservationConfiguration } from '../../../../../types/hubStudentSettings.type';

const language = intl.messages as Record<string, string>;

const PATH = 'reservationStatuses';

const ReservationStatusesOptions = [
  EReservationStatuses.Complete,
  EReservationStatuses.Incomplete,
  EReservationStatuses.Confirmed,
  EReservationStatuses.Preliminary,
  EReservationStatuses.Planned,
  EReservationStatuses.Requested,
  EReservationStatuses.Rejected,
];

function addMandatoryStatus(
  list: HubStudentReservationConfiguration['reservationStatuses']
): EReservationStatuses[] {
  if (!Array.isArray(list)) return [EReservationStatuses.Reservation];
  if (list.includes(EReservationStatuses.Reservation)) return list;
  list.push(EReservationStatuses.Reservation);
  return list;
}

const StudentReservationStatus = () => {
  const dispatch = useAppDispatch();
  const config = useAppSelector(hubStudentSettingsSelector);
  const validationErrors = useAppSelector(hubStudentSettingsErrorsSelector);

  const reservationStatuses: HubStudentReservationConfiguration['reservationStatuses'] =
    config?.studentReservationDisplaySettings?.reservationStatuses;

  const options: SelectProps['options'] = useMemo(
    () =>
      Object.entries(EReservationStatuses)
        .map((entry) => {
          if (!ReservationStatusesOptions.includes(entry[1])) return null;
          return {
            label: entry[0],
            value: entry[1],
          };
        })
        .filter((e) => !!e),
    []
  );

  const onChange = useCallback(
    (statuses: EReservationStatuses[]) => {
      const list = addMandatoryStatus(
        reservationStatuses?.filter(
          (status) => !ReservationStatusesOptions.includes(status)
        )
      );
      if (statuses && statuses.length > 0) {
        list.push(...statuses);
      }
      dispatch(
        hubStudentReservationSettingOnChange({ path: PATH, value: list })
      );

      if (validationErrors != null) {
        dispatch(hubStudentSettingsValidate(false));
      }
    },
    [dispatch, validationErrors, reservationStatuses]
  );

  const onChangeInfo = useCallback(
    (showInfo: boolean) => {
      const list = addMandatoryStatus(
        reservationStatuses?.filter(
          (status) => status !== EReservationStatuses.Info
        )
      );
      if (showInfo) {
        list.push(EReservationStatuses.Info);
      }
      dispatch(
        hubStudentReservationSettingOnChange({ path: PATH, value: list })
      );

      if (validationErrors != null) {
        dispatch(hubStudentSettingsValidate(false));
      }
    },
    [dispatch, validationErrors, reservationStatuses]
  );

  const hasInfo: boolean =
    Array.isArray(reservationStatuses) &&
    reservationStatuses?.includes(EReservationStatuses.Info);

  const selectValues: EReservationStatuses[] | undefined = Array.isArray(
    reservationStatuses
  )
    ? reservationStatuses.filter((status) =>
        ReservationStatusesOptions.includes(status)
      )
    : undefined;

  return (
    <div>
      <div className="row-box">
        <TEInput.LabelWrapper
          label={language.infoReservation}
          tooltip={
            'Show info reservation in the schedule. The first reservation field on the info reservation will be used as title.'
          }
        >
          <div>
            <Segmented<boolean>
              value={hasInfo}
              options={[
                {
                  value: false,
                  label: 'Hide',
                },
                {
                  value: true,
                  label: 'Show',
                },
              ]}
              onChange={onChangeInfo}
            />
          </div>
        </TEInput.LabelWrapper>
      </div>
      <div className="row-box">
        <TEInput.LabelWrapper
          label={getText(
            'integration.calendar.sync_settings.form.reservation_status.label'
          )}
          tooltip={''}
        >
          <Select
            mode="multiple"
            style={{ minWidth: '50em' }}
            options={options}
            value={selectValues}
            onChange={onChange}
            placeholder={getText(
              'integration.calendar.sync_settings.form.reservation_status.placeholder'
            )}
          />
        </TEInput.LabelWrapper>
      </div>
    </div>
  );
};

export default StudentReservationStatus;
